import { useEffect, useState } from 'react';
import { planById } from '@mybooks/plans';
import { AUTH_MODE, useSession } from './session';
import { api, unwrap } from '../api/client';
import { errorText } from '../ui/kit';
import { Logo } from '../ui/Logo';
import { inForce, isPlanLimit, PlanLimitNotice, PlanPicker, useBilling } from '../pages/billing/Subscription';
import { Loading } from '../ui/kit';
import { APP_NAME } from '../lib/brand';
import { browserStartCurrency, START_CURRENCIES } from '../lib/startCurrency';

const LOGO = <Logo size={44} />;

function Frame({ title, sub, wide, children }: { title: string; sub: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className="auth-page">
      <div className={`card auth-card${wide ? ' wide' : ''}`}>
        {LOGO}
        <h1>{title}</h1>
        <p className="muted">{sub}</p>
        {children}
      </div>
    </div>
  );
}

/** Who paid in a checkout, so someone who bought on the website starts on "create your login". */
function useCheckoutDetails(checkout: string | null) {
  const [details, setDetails] = useState<{ email: string | null; plan: string | null } | null>(null);
  useEffect(() => {
    if (!checkout) return;
    unwrap(api.GET('/v1/billing/checkout-sessions/{id}', { params: { path: { id: checkout } } }))
      .then((r) => setDetails({ email: r.data?.email ?? null, plan: r.data?.plan ?? null }), () => setDetails(null));
  }, [checkout]);
  return details;
}

type AccountantInviteDetails = { email: string; company: string; invitedBy: string; status: string };

/** What an accountant invitation link is, before the person has an account (public lookup, ADR 0044). */
function useAccountantInviteDetails(token: string | null): AccountantInviteDetails | null {
  const [details, setDetails] = useState<AccountantInviteDetails | null>(null);
  useEffect(() => {
    if (!token) return;
    unwrap(api.POST('/v1/accountant-invitations/lookup', { body: { token } }))
      .then((r) => setDetails({ email: r.data?.email ?? '', company: r.data?.company_name ?? 'a business', invitedBy: r.data?.invited_by_name ?? 'Someone', status: r.data?.status ?? '' }), () => setDetails(null));
  }, [token]);
  return details;
}

/** Email and password sign-in through Supabase, with account creation. */
export function SignIn({ invited = false, checkout = null, accountantInvite = null }: { invited?: boolean; checkout?: string | null; accountantInvite?: string | null }) {
  const session = useSession();
  const [mode, setMode] = useState<'in' | 'up'>(checkout || accountantInvite ? 'up' : 'in');
  const [email, setEmail] = useState('');
  const paid = useCheckoutDetails(checkout);
  useEffect(() => { if (paid?.email) setEmail((e) => e || paid.email || ''); }, [paid]);
  // An accountant invitation names the address it was sent to, so it is filled in, not retyped.
  const accountant = useAccountantInviteDetails(accountantInvite);
  useEffect(() => { if (accountant?.email) setEmail(accountant.email); }, [accountant]);
  const emailLocked = !!accountant?.email && accountant.status === 'open';
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (AUTH_MODE === 'unconfigured') {
    return <Frame title={`${APP_NAME}`} sub="Sign-in is not configured for this build. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY and rebuild."><span /></Frame>;
  }
  if (sent) return <Frame title="Check your email" sub={`We sent a confirmation link to ${email}. Open it, then sign in here.${invited ? ' The link brings you back to your invitation.' : checkout ? ' Your plan is added to your account when you do.' : ''}`}><button className="btn primary" onClick={() => { setSent(false); setMode('in'); }}>Back to sign in</button></Frame>;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Enter an email like name@example.com'); return; }
    if (password.length < (mode === 'up' ? 8 : 1)) { setError(mode === 'up' ? 'Use at least 8 characters' : 'Enter your password'); return; }
    setBusy(true);
    try {
      if (mode === 'in') await session.signIn(email, password);
      else if ((await session.signUp(email, password, name.trim())) === 'confirm-email') setSent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const plan = planById(paid?.plan ?? '');
  const sub = accountant && accountant.status === 'open'
    ? `${accountant.invitedBy} invited you to be the accountant of ${accountant.company}. ${mode === 'up' ? 'Create your account' : 'Sign in'} with ${accountant.email}, then set up your practice.`
    : invited
    ? `${mode === 'in' ? 'Sign in' : 'Create your account'} with the email address your invitation was sent to, and you join the company straight after.`
    : checkout
      ? `Thank you for subscribing${plan ? ` to ${plan.name}` : ''}. ${mode === 'up' ? 'Create your login' : 'Sign in'} and your plan is added to your account.`
      : mode === 'in' ? 'Bookkeeping for small companies' : 'Then set up your first company';
  return (
    <Frame title={checkout && mode === 'up' ? 'Create your login' : mode === 'in' ? `Sign in to ${APP_NAME}` : 'Create your account'} sub={sub}>
      <form className="auth-form" onSubmit={submit} noValidate>
        {mode === 'up' && <div className="field"><label htmlFor="su-name">Your name</label><input className="input" id="su-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} /></div>}
        <div className="field"><label htmlFor="si-email">Email</label><input className="input" id="si-email" type="email" autoComplete="email" autoFocus={!emailLocked} readOnly={emailLocked} value={email} onChange={(e) => setEmail(e.target.value)} />{emailLocked && <span className="hint">The address your invitation was sent to</span>}</div>
        <div className="field"><label htmlFor="si-password">Password</label><input className="input" id="si-password" type="password" autoComplete={mode === 'in' ? 'current-password' : 'new-password'} value={password} onChange={(e) => setPassword(e.target.value)} /></div>
        {error && <div className="error" role="alert">{error}</div>}
        <button className="btn primary" type="submit" disabled={busy}>{busy ? 'Please wait…' : mode === 'in' ? 'Sign in' : 'Create account'}</button>
      </form>
      <button className="link-btn" onClick={() => { setMode(mode === 'in' ? 'up' : 'in'); setError(''); }}>{mode === 'in' ? `New to ${APP_NAME}? Create an account` : 'Already have an account? Sign in'}</button>
    </Frame>
  );
}

/** Shown when the signed-in person has no business yet, or (in development) to pick one. */
/**
 * Someone signed in who cannot add a business yet: they have never subscribed, or their plan has
 * ended. Every business needs an owner with a plan in force (ADR 0040), so the plan comes first.
 */
function needsPlan(billing: ReturnType<typeof useBilling>) {
  return AUTH_MODE === 'supabase' && billing.data?.configured === true && billing.data.company_limit === 0;
}

export function FirstBusiness() {
  const billing = useBilling();
  if (AUTH_MODE === 'supabase' && billing.isLoading) return <div className="auth-page"><Loading /></div>;
  if (needsPlan(billing)) return <ChoosePlan ended={!!billing.data?.subscription} />;
  return <NewBusiness />;
}

/** The plan picker on its own, before the first business or after the plan has ended. */
function ChoosePlan({ ended, businessName }: { ended: boolean; businessName?: string }) {
  const session = useSession();
  const others = session.tenants.filter((t) => t.planInForce && t.id !== session.tenant?.id);
  return (
    <Frame
      wide
      title={ended ? 'Your plan has ended' : 'Choose your plan'}
      sub={`${ended ? `Choose a plan to use ${businessName ?? 'your companies'} again; nothing has been deleted.` : 'Pick a plan to set up your company.'} Every plan includes unlimited users. You pay securely with Stripe and come straight back.`}
    >
      <PlanPicker />
      <OtherBusinesses others={others} />
      <button className="link-btn" onClick={() => { void session.signOut(); }}>Sign out</button>
    </Frame>
  );
}

function OtherBusinesses({ others }: { others: { id: string; name: string }[] }) {
  const session = useSession();
  if (others.length === 0) return null;
  return (
    <div className="auth-form">
      <span className="muted">Or open another company</span>
      {others.map((t) => <button key={t.id} type="button" className="btn" onClick={() => session.selectTenant(t.id)}>{t.name}</button>)}
    </div>
  );
}

/**
 * The business in use has no owner with a plan in force, so the API refuses its screens (ADR 0040).
 * Its owner can choose a plan here; anyone else is told to ask them.
 */
export function PlanRequired() {
  const session = useSession();
  const billing = useBilling();
  const tenant = session.tenant!;
  if (tenant.role === 'owner') {
    if (billing.isLoading) return <div className="auth-page"><Loading /></div>;
    return <ChoosePlan ended={!!billing.data?.subscription && !inForce(billing.data.subscription)} businessName={tenant.name} />;
  }
  const others = session.tenants.filter((t) => t.planInForce && t.id !== tenant.id);
  return (
    <Frame title={`${tenant.name} is paused`} sub={`Its owner has no plan in force, so nobody can use it for now. Ask them to choose a plan in ${APP_NAME}; everything is kept until then.`}>
      <OtherBusinesses others={others} />
      <button className="link-btn" onClick={() => { void session.signOut(); }}>Sign out</button>
    </Frame>
  );
}

function NewBusiness() {
  const session = useSession();
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState(browserStartCurrency);
  const [devId, setDevId] = useState('');
  const [error, setError] = useState('');
  const [overLimit, setOverLimit] = useState(false);
  const [busy, setBusy] = useState(false);
  const failed = (err: unknown) => { setOverLimit(isPlanLimit(err)); setError(errorText(err)); setBusy(false); };
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Enter the company name'); return; }
    setBusy(true);
    try { await session.createBusiness(name.trim(), currency); } catch (err) { failed(err); }
  };
  return (
    <Frame title="Set up your company" sub={AUTH_MODE === 'dev' ? 'Development sign-in: this talks to wrangler dev with the dev-only headers.' : `You will be its owner. ${APP_NAME} sets up the standard US chart of accounts.`}>
      <form className="auth-form" onSubmit={create} noValidate>
        <div className="field"><label htmlFor="fb-name">Company name</label><input className="input" id="fb-name" autoFocus value={name} onChange={(e) => { setName(e.target.value); setError(''); setOverLimit(false); }} /></div>
        <div className="field"><label htmlFor="fb-currency">Currency</label><select className="input" id="fb-currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>{START_CURRENCIES.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}</select><span className="hint">The currency your books are kept in. It cannot change once there are entries.</span></div>
        {error && (overLimit ? <PlanLimitNotice message={error} inShell={false} /> : <div className="error" role="alert">{error}</div>)}
        <button className="btn primary" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create company'}</button>
      </form>
      {overLimit && <PlanPicker />}
      {AUTH_MODE === 'dev' && (
        <form className="auth-form" onSubmit={(e) => { e.preventDefault(); if (/^[0-9a-f-]{36}$/i.test(devId.trim())) session.addDevTenant(devId.trim(), 'Existing company'); }}>
          <div className="field"><label htmlFor="fb-id">Or use an existing tenant id</label><input className="input" id="fb-id" value={devId} onChange={(e) => setDevId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" /></div>
          <button className="btn" type="submit">Use this company</button>
        </form>
      )}
      {session.archived.length > 0 && (
        <div className="auth-form">
          <span className="muted">Archived companies you own</span>
          {session.archived.map((a) => (
            <button key={a.id} type="button" className="btn" disabled={busy} onClick={() => { setBusy(true); session.restoreBusiness(a.id).catch(failed); }}>Restore {a.name}</button>
          ))}
        </div>
      )}
      {AUTH_MODE === 'supabase' && <button className="link-btn" onClick={() => { void session.signOut(); }}>Sign out</button>}
    </Frame>
  );
}
